// Paso Referidos: un formulario que guarda un referido a la vez, la lista con lo guardado y «Editar»
// para corregirlo en el mismo formulario. «Continuar» solo avanza; nunca detiene la venta.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight, Pencil } from 'lucide-react';
import { Button, Input, Loading, PhoneInput, Skeleton } from '@/kit';
import { fromStoredPhone } from '@/lib/phoneMx';
import type { CustomerReferral } from '@/api/services/retail-wizard/contract';
import { StatusPill } from '@/shared/patterns/Pills';
import { hasAccountOpeningMark } from '../accountOpening';
import {
    useSaveSessionReferrals,
    useSessionReferrals,
    useSkipReferrals,
    useUpdateSessionReferral,
    useWizardSession,
} from '../api/useWizard';
import { wizardErrorCode, wizardErrorMsg } from '../api/wizardError';
import { QueryErrorNotice } from '../components/QueryErrorNotice';
import {
    WizardCard,
    WizardCardBody,
    WizardCardHead,
    WizardSection,
} from '../components/WizardCard';
import { WizardBackLink, WizardFootNav } from '../components/WizardFootNav';
import { WizardWarningNotice } from '../components/WizardWarningNotice';
import { formatPhone } from '../phoneFormat';
import {
    REFERRALS_COPY as COPY,
    blankDraft,
    customerFirstName,
    hasErrors,
    isBlankDraft,
    serverDraftError,
    sortReferrals,
    STALE_SESSION_CODES,
    toReferralInput,
    validateDraft,
    type ReferralDraft,
    type ReferralField,
    type ServerDraftError,
} from '../referrals';
import {
    formOf,
    isReferralNotFound,
    referralPatchOf,
} from '@/shared/patterns/CustomerReferrals/customerReferrals';
import { previousStepSlug } from '../steps';
import type { WizardStepSlug } from '../types';

export interface ReferralsSlots {
    /** Promos de referido vigentes; en una apertura sin venta, solo las de abrir cuenta. */
    promotionsBanner?: (ctx: { opening: boolean; customerName: string }) => ReactNode;
}

type Touched = Partial<Record<ReferralField, boolean>>;

export function ReferralsStep({ sessionId, slots }: { sessionId: string; slots?: ReferralsSlots }) {
    const navigate = useNavigate();
    const { data: session, refresh } = useWizardSession(sessionId);
    const existing = useSessionReferrals(sessionId);
    const save = useSaveSessionReferrals(sessionId);
    const update = useUpdateSessionReferral(sessionId);
    const skip = useSkipReferrals(sessionId);

    const [draft, setDraft] = useState<ReferralDraft>(blankDraft);
    const [touched, setTouched] = useState<Touched>({});
    const [submitted, setSubmitted] = useState(false);
    const [serverError, setServerError] = useState<ServerDraftError | null>(null);
    const [editing, setEditing] = useState<CustomerReferral | null>(null);
    const [savedIds, setSavedIds] = useState<ReadonlySet<string>>(() => new Set());
    const [pendingNotice, setPendingNotice] = useState(false);
    const [focusTick, setFocusTick] = useState(0);
    const formRef = useRef<HTMLFieldSetElement>(null);

    // Tras un guardado rechazado lleva el foco al primer campo marcado; al editar, al nombre.
    useEffect(() => {
        if (focusTick === 0) return;
        const form = formRef.current;
        const target =
            form?.querySelector<HTMLElement>('[aria-invalid="true"]') ??
            form?.querySelector<HTMLElement>('input');
        target?.focus();
    }, [focusTick]);

    if (!session) return <Loading size="lg" />;

    const customerName = customerFirstName(session);
    const known = existing.data ?? [];
    const others = editing ? known.filter((r) => r.referralId !== editing.referralId) : known;
    const localErrors = validateDraft(draft, {
        customerPhone: fromStoredPhone(session.phone),
        customerName,
        knownPhones: others.map((r) => fromStoredPhone(r.phone)),
    });
    const unsaved = editing ? referralPatchOf(editing, draft) !== null : !isBlankDraft(draft);
    const busy = save.isPending || update.isPending || skip.isPending;
    const opening = hasAccountOpeningMark(session);
    const nextSlug: WizardStepSlug = 'welcome';
    const backSlug = previousStepSlug('referrals', session);

    const fieldError = (field: ReferralField): string | undefined => {
        const local = localErrors[field];
        if (local && (submitted || touched[field])) return local;
        return serverError?.field === field ? serverError.message : undefined;
    };

    const resetForm = (): void => {
        setDraft(blankDraft());
        setTouched({});
        setSubmitted(false);
        setServerError(null);
        setEditing(null);
    };

    const changeField = (field: ReferralField, value: string): void => {
        setDraft((prev) => ({ ...prev, [field]: value }));
        if (serverError?.field === field) setServerError(null);
        setPendingNotice(false);
    };

    const startEdit = (referral: CustomerReferral): void => {
        setDraft(formOf(referral));
        setTouched({});
        setSubmitted(false);
        setServerError(null);
        setPendingNotice(false);
        setEditing(referral);
        setFocusTick((n) => n + 1);
    };

    const handleFailure = (error: unknown, fallback: string): void => {
        if (STALE_SESSION_CODES.has(wizardErrorCode(error) ?? '')) void refresh();
        toast.error(wizardErrorMsg(error, fallback));
    };

    const rejectSave = (error: unknown): void => {
        const located = serverDraftError(error, customerName);
        if (located) {
            setServerError(located);
            setFocusTick((n) => n + 1);
            return;
        }
        if (editing && isReferralNotFound(error)) {
            toast.error(COPY.notFound);
            void existing.refetch();
            resetForm();
            return;
        }
        handleFailure(error, COPY.saveFailed);
    };

    const onSave = async (): Promise<void> => {
        setSubmitted(true);
        setServerError(null);
        setPendingNotice(false);
        if (hasErrors(localErrors)) {
            setFocusTick((n) => n + 1);
            return;
        }
        try {
            if (editing) {
                const patch = referralPatchOf(editing, draft);
                if (!patch) toast.info(COPY.noChanges);
                else if (editing.referralId) {
                    await update.mutateAsync({ referralId: editing.referralId, patch });
                    toast.success(COPY.updated);
                }
            } else {
                const created = await save.mutateAsync([toReferralInput(draft)]);
                const createdId = created.at(0)?.referralId;
                if (createdId) setSavedIds((prev) => new Set(prev).add(createdId));
                toast.success(COPY.saved);
            }
        } catch (error) {
            rejectSave(error);
            return;
        }
        resetForm();
    };

    // Guardar ya registró el paso; si no se guardó nada, el skip es lo que lo registra.
    const proceed = async (): Promise<void> => {
        if (savedIds.size === 0) {
            try {
                await skip.mutateAsync(undefined);
            } catch (error) {
                handleFailure(error, COPY.skipFailed);
                return;
            }
        }
        await navigate({ to: '/wizard/s/$sessionId/$step', params: { sessionId, step: nextSlug } });
    };

    const onContinue = (): void => {
        if (unsaved) {
            setPendingNotice(true);
            return;
        }
        void proceed();
    };

    return (
        <WizardCard>
            <WizardCardHead
                eyebrow={COPY.eyebrow}
                title={COPY.title}
                sub={COPY.sub(customerName)}
            />

            <WizardCardBody className="flex flex-col gap-6">
                {slots?.promotionsBanner?.({ opening, customerName })}

                <ReferralsList
                    customerName={customerName}
                    referrals={known}
                    savedIds={savedIds}
                    editingId={editing?.referralId ?? null}
                    disabled={busy}
                    onEdit={startEdit}
                    loading={existing.isLoading}
                    error={existing.error}
                    onRetry={() => void existing.refetch()}
                    retrying={existing.isFetching}
                />

                <WizardSection label={editing ? COPY.editTitle : COPY.addTitle}>
                    <p className="text-[12.5px] text-muted-foreground">
                        {editing ? COPY.editHint : COPY.addHint}
                    </p>

                    {pendingNotice && (
                        <WizardWarningNotice>
                            <p className="font-medium">{COPY.pendingTitle}</p>
                            <p>{COPY.pendingText}</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                <Button
                                    size="sm"
                                    onClick={() => {
                                        setPendingNotice(false);
                                        setFocusTick((n) => n + 1);
                                    }}
                                >
                                    {COPY.keepCapturing}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    isLoading={skip.isPending}
                                    disabled={busy}
                                    onClick={() => void proceed()}
                                >
                                    {COPY.continueAnyway}
                                </Button>
                            </div>
                        </WizardWarningNotice>
                    )}

                    <fieldset
                        ref={formRef}
                        className={
                            'grid gap-2 rounded-lg border bg-muted/35 p-3 ' +
                            (editing ? 'border-primary/50' : 'border-border')
                        }
                    >
                        <legend className="sr-only">
                            {editing ? COPY.editTitle : COPY.addTitle}
                        </legend>
                        <Input
                            label={COPY.nameLabel}
                            placeholder={COPY.namePlaceholder}
                            autoComplete="off"
                            maxLength={120}
                            value={draft.name}
                            error={fieldError('name')}
                            onChange={(e) => {
                                changeField('name', e.target.value);
                            }}
                            onBlur={() => {
                                setTouched((t) => ({ ...t, name: true }));
                            }}
                        />
                        <PhoneInput
                            label={COPY.phoneLabel}
                            placeholder={COPY.phonePlaceholder}
                            autoComplete="off"
                            value={draft.phone}
                            error={fieldError('phone')}
                            onValueChange={(digits) => {
                                changeField('phone', digits);
                            }}
                            onBlur={() => {
                                setTouched((t) => ({ ...t, phone: true }));
                            }}
                        />
                        <Input
                            label={COPY.emailLabel}
                            type="email"
                            placeholder={COPY.emailPlaceholder}
                            autoComplete="off"
                            value={draft.email}
                            error={fieldError('email')}
                            onChange={(e) => {
                                changeField('email', e.target.value);
                            }}
                            onBlur={() => {
                                setTouched((t) => ({ ...t, email: true }));
                            }}
                        />
                        <div className="flex flex-wrap justify-end gap-2 pt-1">
                            {editing && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={busy}
                                    onClick={resetForm}
                                >
                                    {COPY.cancelEdit}
                                </Button>
                            )}
                            <Button
                                size="sm"
                                disabled={busy}
                                isLoading={save.isPending || update.isPending}
                                onClick={() => void onSave()}
                            >
                                {editing ? COPY.saveChanges : COPY.saveReferral}
                            </Button>
                        </div>
                    </fieldset>
                </WizardSection>
            </WizardCardBody>

            <WizardFootNav
                back={
                    backSlug ? <WizardBackLink sessionId={sessionId} step={backSlug} /> : undefined
                }
            >
                <Button
                    disabled={busy}
                    isLoading={skip.isPending && !pendingNotice}
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={onContinue}
                >
                    {COPY.continue}
                </Button>
            </WizardFootNav>
        </WizardCard>
    );
}

function initials(name: string): string {
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w.charAt(0))
        .join('')
        .toUpperCase();
}

function ReferralsList({
    customerName,
    referrals,
    savedIds,
    editingId,
    disabled,
    onEdit,
    loading,
    error,
    onRetry,
    retrying,
}: {
    customerName: string;
    referrals: CustomerReferral[];
    savedIds: ReadonlySet<string>;
    editingId: string | null;
    disabled: boolean;
    onEdit: (referral: CustomerReferral) => void;
    loading: boolean;
    error: unknown;
    onRetry: () => void;
    retrying: boolean;
}) {
    const title = COPY.existingTitle(customerName);
    const label = referrals.length > 0 ? `${title} (${String(referrals.length)})` : title;

    return (
        <WizardSection label={label}>
            {loading ? (
                <Skeleton lines={2} className="h-8" />
            ) : error ? (
                <QueryErrorNotice
                    title={COPY.existingError}
                    error={error}
                    fallback="Revisa tu conexión e intenta de nuevo."
                    onRetry={onRetry}
                    retrying={retrying}
                    className="mx-0 mb-0 max-w-none"
                />
            ) : referrals.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-3.5 text-center text-[12.5px] text-muted-foreground">
                    {COPY.existingEmpty(customerName)}
                </div>
            ) : (
                <ul
                    aria-label={title}
                    className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card"
                >
                    {sortReferrals(referrals).map((r) => {
                        const name = r.name ?? '';
                        const phone = r.phone ? formatPhone(r.phone) : '';
                        const id = r.referralId;
                        return (
                            <li
                                key={id ?? `${name}-${phone}`}
                                className={
                                    'flex items-center gap-2.5 px-3 py-2.5 ' +
                                    (id && id === editingId ? 'bg-muted/50' : '')
                                }
                            >
                                <span
                                    aria-hidden
                                    className="flex size-8 flex-none items-center justify-center rounded-full bg-muted text-[11px] font-bold text-muted-foreground"
                                >
                                    {initials(name)}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="flex items-center gap-1.5 text-[13px] font-semibold">
                                        <span className="truncate">{name}</span>
                                        {id && savedIds.has(id) && (
                                            <StatusPill tone="success">{COPY.savedPill}</StatusPill>
                                        )}
                                    </p>
                                    <p className="truncate text-[11.5px] tabular-nums text-muted-foreground">
                                        {r.email ? `${phone} · ${r.email}` : phone}
                                    </p>
                                </div>
                                {id && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={disabled || id === editingId}
                                        aria-label={COPY.editLabel(name)}
                                        leftIcon={<Pencil className="size-3.5" aria-hidden />}
                                        onClick={() => {
                                            onEdit(r);
                                        }}
                                    >
                                        {COPY.edit}
                                    </Button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </WizardSection>
    );
}
